import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/lib/analytics';
import { useLanguage } from '@/lib/i18n/LanguageContext';

/** Max time we wait for <SEOHead> to swap the document title before sending. */
const TITLE_WAIT_MS = 1200;
/** Absolute deadline: send even if the placeholder title never resolves. */
const TITLE_MAX_WAIT_MS = 5000;

/**
 * Placeholder titles rendered by loading states (e.g. blog posts emit a
 * generic head while the article is fetched). Sending these to GA would
 * report every article under the same generic title, so we keep waiting
 * for the real title instead.
 */
const PLACEHOLDER_TITLES = ['i-Rent.net'];
const isPlaceholderTitle = (title: string) =>
  PLACEHOLDER_TITLES.includes(title.trim());


/**
 * Sends a GA4 `page_view` for the initial load and every SPA route change.
 * GA's own auto page_view is disabled (`send_page_view: false`) so there are
 * no duplicates. We wait briefly for the route's <title> to settle so the
 * reported page_title matches the page the user actually landed on.
 */
export function usePageViewTracking() {
  const { pathname, search } = useLocation();
  const { language } = useLanguage();
  const lastSent = useRef<string | null>(null);

  useEffect(() => {
    const path = pathname + search;
    if (lastSent.current === path) return;

    const titleAtStart = document.title;
    let done = false;
    let observer: MutationObserver | null = null;

    const send = () => {
      if (done) return;
      done = true;
      observer?.disconnect();
      window.clearTimeout(timeoutId);
      window.clearTimeout(hardTimeoutId);
      if (lastSent.current === path) return;
      lastSent.current = path;
      trackPageView({ path, title: document.title, language });
    };

    // Soft deadline: send unless the head still shows a loading placeholder.
    const timeoutId = window.setTimeout(() => {
      if (!isPlaceholderTitle(document.title)) send();
    }, TITLE_WAIT_MS);
    // Hard deadline: send regardless, so a stuck placeholder still reports.
    const hardTimeoutId = window.setTimeout(send, TITLE_MAX_WAIT_MS);

    const titleEl = document.querySelector('title');
    if (titleEl && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        if (document.title !== titleAtStart && !isPlaceholderTitle(document.title)) send();
      });
      observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    }


    return () => {
      observer?.disconnect();
      window.clearTimeout(timeoutId);
      window.clearTimeout(hardTimeoutId);
    };
  }, [pathname, search, language]);
}
